import type { File } from "schemas/generated/file";

import * as React from "react";

import { DebouncedInput } from "@editor/components/common/designSystem/Input";
import { trpc } from "@editor/utils/trpc";

import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Plus, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

import PageRenderer from "../PageRenderer";

function PageSelectionCardSkeleton() {
  return (
    <div className="flex items-center gap-3 px-2 py-2 rounded-md">
      <div className="flex-shrink-0 w-[70px] h-[40px] rounded border border-border overflow-hidden">
        <Skeleton className="w-full h-full rounded" />
      </div>
      <div className="flex flex-col gap-0.5 flex-1 min-w-0">
        <Skeleton className="h-4 w-24 rounded" />
        <Skeleton className="h-3 w-16 rounded" />
      </div>
    </div>
  );
}

function PageSelectionCard({
  page,
  handlePageSelectionCardClick,
}: {
  page: Pick<File, "id" | "name" | "pageRoute" | "createdAt" | "updatedAt">;
  handlePageSelectionCardClick: () => void;
}) {
  if (!page.pageRoute) {
    return null;
  }

  return (
    <div
      className="flex items-center gap-3 px-2 py-2 rounded-md cursor-pointer hover:bg-selectable-hover"
      onClick={handlePageSelectionCardClick}
    >
      <div className="flex-shrink-0 w-[70px] h-[40px] rounded border border-border overflow-hidden">
        <PageRenderer
          pageRoute={page.pageRoute}
          previewThumbnail
          showControls={false}
          className="pointer-events-none h-full w-full"
        />
      </div>
      <div className="flex flex-col gap-0.5 flex-1 min-w-0">
        <div className="text-sm font-medium truncate">
          {page.name ?? "Untitled Page"}
        </div>
        <div className="text-xs text-muted truncate">{page.pageRoute}</div>
      </div>
    </div>
  );
}

function PageSelectionDropdown({
  projectId,
  handlePageSelectionCardClick,
  currentPageId,
  hideNewPageButton = false,
}: {
  projectId: string;
  handlePageSelectionCardClick: (page: Pick<File, "id" | "pageRoute">) => void;
  currentPageId?: string;
  hideNewPageButton?: boolean;
}) {
  const [searchQuery, setSearchQuery] = React.useState("");
  const navigate = useNavigate();

  const { data: pages, isLoading } = trpc.nextJSProject.listPages.useQuery(
    { projectId: projectId },
    { enabled: Boolean(projectId) },
  );

  const filteredPages = React.useMemo(() => {
    if (!pages) {
      return pages;
    }

    // filter out the current page if currentPageId is provided
    let result = pages;
    if (currentPageId) {
      result = result.filter((page) => page.id !== currentPageId);
    }

    if (!searchQuery) {
      return result;
    }

    const query = searchQuery.toLowerCase();
    return result.filter((page) => {
      const name = page.name?.toLowerCase() ?? "untitled page";
      const route = page.pageRoute?.toLowerCase() ?? "";
      return name.includes(query) || route.includes(query);
    });
  }, [pages, searchQuery, currentPageId]);

  return (
    <div className="flex flex-col gap-3">
      <DebouncedInput
        size="sm"
        placeholder="Search"
        startEnhancer={<Search size={14} />}
        value={searchQuery}
        onValueChange={setSearchQuery}
      />
      <div className="flex flex-col gap-1">
        {!hideNewPageButton && (
          <div
            className="flex items-center gap-3 px-2 py-2 rounded-md cursor-pointer hover:bg-selectable-hover"
            onClick={() => {
              navigate(`/shops/${projectId}/site/page/new?menu-type=new`);
            }}
          >
            <div className="flex-shrink-0 w-[70px] h-[40px] rounded border border-border border-dashed bg-sheet flex items-center justify-center">
              <Plus size={16} className="text-muted" />
            </div>
            <div className="flex flex-col gap-0.5 flex-1 min-w-0">
              <div className="text-sm font-medium truncate">New page</div>
            </div>
          </div>
        )}

        {isLoading
          ? Array.from({ length: 3 }).map((_, index) => (
              <PageSelectionCardSkeleton key={index} />
            ))
          : filteredPages?.map((page) => (
              <PageSelectionCard
                key={page.id}
                page={page}
                handlePageSelectionCardClick={() => {
                  handlePageSelectionCardClick(page);
                }}
              />
            ))}
      </div>
    </div>
  );
}

export default PageSelectionDropdown;
